import React from 'react';
import { Link } from 'react-router';
import { AssetUrls } from 'config/assets';
import './WantListCardOverlay.css';

interface WantListCardOverlayProps {
  cardName: string;
  quantity: number;
  addedCount: number;
  onAddToTrade: () => void;
  onViewCard: () => void;
  onClose?: () => void;
  isMobile?: boolean;
  /**
   * 'trade' (default): other users' profiles — shows the "Add to Trade" button.
   * 'manage': self-view — shows a "Manage" link (no trade action, no "Added"/quantity
   * state, no wants flyout).
   * 'view': the holder has turned off incoming trade offers — everything renders
   * except the trade action. Not the same as 'manage': the viewer is still on
   * someone else's profile and can open the card, they just cannot offer on it.
   */
  mode?: 'trade' | 'manage' | 'view';
  /** Destination for the "Manage" link (manage mode only). Defaults to the Want List tab. */
  manageHref?: string;
  /** Set when the card cannot be added (it has no price): the button is disabled and this is its tooltip. */
  addDisabledReason?: string;
  /**
   * A visible line under the action (the offer builder's accept-condition
   * reason, "They accept NM, LP only"): shown whenever given.
   */
  addDisabledNote?: string;
  /**
   * Makes the "✓ Added" state a button that removes the card (the offer
   * builder's grid, Asana 1219902279124885 Decision 4). Omitted, the state
   * stays the static chip the profile tabs show.
   */
  onRemove?: () => void;
  /**
   * Which way the Add to Trade arrow points: towards the cart the card lands
   * in. 'right' (default) is the profile's "Add to Trade ▷"; 'left' puts a
   * mirrored arrow before the text ("◁ Add to Trade") for the offer builder's
   * receive grid, whose cart sits to the left of the list.
   */
  addArrow?: 'right' | 'left';
}

export const WantListCardOverlay: React.FC<WantListCardOverlayProps> = ({
  cardName,
  quantity,
  addedCount,
  onAddToTrade,
  onViewCard,
  onClose,
  isMobile = false,
  mode = 'trade',
  manageHref = '/my-cards/want-list',
  addDisabledReason,
  addDisabledNote,
  onRemove,
  addArrow = 'right',
}) => {
  const remainingQuantity = quantity - addedCount;
  const allAdded = remainingQuantity <= 0;
  const isManage = mode === 'manage';
  const isViewOnly = mode === 'view';

  return (
    <div className="want-list-card-overlay visible">
      {/* Mobile close button */}
      {isMobile && onClose && (
        <button className="want-list-card-overlay-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      )}

      {/* Arrow button to view card details */}
      <button
        className="want-list-card-overlay-arrow"
        onClick={(e) => {
          e.stopPropagation();
          onViewCard();
        }}
        aria-label="View card details"
      >
        <img
          src={AssetUrls.search.arrowDiagonal}
          alt="View"
          className="want-list-card-overlay-arrow-icon"
        />
      </button>

      {/* Quantity badge if more than 1 remaining (trade mode only) */}
      {!isManage && remainingQuantity > 1 && (
        <div className="want-list-card-overlay-quantity">
          {remainingQuantity} left
        </div>
      )}

      {/* Card title */}
      <span
        className="want-list-card-overlay-title"
        onClick={(e) => {
          e.stopPropagation();
          onViewCard();
        }}
        role="link"
        tabIndex={0}
      >
        {cardName}
      </span>

      {/* Primary action: Manage link (self-view) or Add to Trade / Added (other profiles) */}
      {isViewOnly ? null : isManage ? (
        <Link
          to={manageHref}
          className="want-list-card-overlay-button"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={AssetUrls.icons.navigation.collection}
            alt=""
            className="want-list-card-overlay-button-icon"
          />
          <span>Manage</span>
        </Link>
      ) : allAdded ? (
        onRemove ? (
          <button
            type="button"
            className="want-list-card-overlay-added want-list-card-overlay-added--button"
            aria-label="Remove from trade"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <span className="want-list-card-overlay-checkmark">✓</span>
            <span>Added</span>
          </button>
        ) : (
          <div className="want-list-card-overlay-added">
            <span className="want-list-card-overlay-checkmark">✓</span>
            <span>Added</span>
          </div>
        )
      ) : (
        <button
          className={`want-list-card-overlay-button${
            addArrow === 'left' ? ' want-list-card-overlay-button--arrow-left' : ''
          }`}
          disabled={!!addDisabledReason}
          title={addDisabledReason}
          onClick={(e) => {
            e.stopPropagation();
            if (!addDisabledReason) onAddToTrade();
          }}
        >
          {addArrow === 'left' && (
            <img
              src={AssetUrls.icons.navigation.sending}
              alt=""
              className="want-list-card-overlay-button-icon want-list-card-overlay-button-icon--left"
            />
          )}
          <span>Add to Trade</span>
          {addArrow !== 'left' && (
            <img
              src={AssetUrls.icons.navigation.sending}
              alt=""
              className="want-list-card-overlay-button-icon"
            />
          )}
        </button>
      )}

      {addDisabledNote && <span className="want-list-card-overlay-note">{addDisabledNote}</span>}
    </div>
  );
};

export default WantListCardOverlay;
